import {
  useToast,
  Box,
  Image,
  Skeleton,
  VStack,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { useAppDispatch, useAppSelector } from "../../../Store/hooks";
import { ApolloError, useMutation } from "@apollo/client";
import {
  SET_FAVORITE,
  DELETE_PROJECT,
  LOAD_PROJECTS,
  SET_PUBLISH,
} from "../../Queries";
import { setProjectId } from "../../../Store/GlobalReducer";
import { setModalOpen, ModalsName } from "../../../Store/ModalsReducer";
import { ProjectPayload, ProjectProps } from "./Types";
import AddToFavImage from "../../Images/addToFav.png";
import FavoriteImage from "../../Images/fav.png";
import PublicateImage from "../../Images/publicate.png";
import PublishedImage from "../../Images/published.png";
import DeleteImage from "../../Images/delete.png";

const Project = (props: ProjectProps) => {
  const dispatch = useAppDispatch();

  const toast = useToast({
    variant: "left-accent",
    position: "top-right",
  });

  const user = useAppSelector((state) => state.global.user);

  const [deleteProjectMutation, deleteInfo] = useMutation(DELETE_PROJECT, {
    onError: (err: ApolloError) => {
      console.log(err);
      toast({
        title: `Ошибка: ${err.message}`,
        status: "error",
      });
    },
    onCompleted: () => {
      toast({ title: "Проект удален!", status: "success" });
    },
    update: (cache) => {
      const favProjectsData = cache.readQuery<{ projects: ProjectPayload }>({
        query: LOAD_PROJECTS,
        variables: { published: false, favorite: true },
      });
      if (favProjectsData && favProjectsData.projects) {
        const { projects } = favProjectsData;
        const total = projects.total ? projects.total - 1 : 0;
        cache.writeQuery({
          query: LOAD_PROJECTS,
          variables: { published: false, favorite: true },
          data: {
            projects: {
              items: projects.items.filter(
                (project: ProjectProps) => project.id !== props.id
              ),
              total,
            },
          },
        });
      }

      const pubProjectsData = cache.readQuery<{ projects: ProjectPayload }>({
        query: LOAD_PROJECTS,
        variables: { published: true, favorite: false },
      });
      if (pubProjectsData && pubProjectsData.projects) {
        const { projects } = pubProjectsData;
        const total = projects.total ? projects.total - 1 : 0;
        cache.writeQuery({
          query: LOAD_PROJECTS,
          variables: { published: true, favorite: false },
          data: {
            projects: {
              items: projects.items.filter(
                (project: ProjectProps) => project.id !== props.id
              ),
              total,
            },
          },
        });
      }

      const projectsData = cache.readQuery<{ projects: ProjectPayload }>({
        query: LOAD_PROJECTS,
        variables: { published: false, favorite: false },
      });
      if (projectsData && projectsData.projects) {
        const { projects } = projectsData;
        const total = projects.total ? projects.total - 1 : 0;
        cache.writeQuery({
          query: LOAD_PROJECTS,
          variables: { published: false, favorite: false },
          data: {
            projects: {
              items: projects.items.filter(
                (project: ProjectProps) => project.id !== props.id
              ),
              total,
            },
          },
        });
      }
    },
  });

  const deleteProject = (id: number) => {
    deleteProjectMutation({
      variables: { id },
    });
  };

  const [setFav, setFavInfo] = useMutation(SET_FAVORITE, {
    onError: (err: ApolloError) => {
      console.log(err);
      toast({
        title: `Ошибка: ${err.message}`,
        status: "error",
      });
    },
    onCompleted: () => {
      toast({
        title: props.isFavorite
          ? "Проект удален из избранного!"
          : "Проект добавлен в избранное!",
        status: "success",
      });
    },
    update: (cache) => {
      const favProjectsData = cache.readQuery<{ projects: ProjectPayload }>({
        query: LOAD_PROJECTS,
        variables: { published: false, favorite: true },
      });
      if (favProjectsData && favProjectsData.projects) {
        const { projects } = favProjectsData;
        let newItems: ProjectProps[] = [];
        let total = projects.total ? projects.total : 0;
        if (props.isFavorite) {
          // delete from favorites
          newItems = projects.items.filter(
            (project: ProjectProps) => project.id !== props.id
          );
          total -= 1;
        } else {
          newItems = [
            { ...props, isFavorite: !props.isFavorite },
            ...projects.items,
          ];
          total += 1;
        }
        cache.writeQuery({
          query: LOAD_PROJECTS,
          variables: { published: false, favorite: true },
          data: {
            projects: {
              items: newItems,
              total,
            },
          },
        });
      }

      const pubProjectsData = cache.readQuery<{ projects: ProjectPayload }>({
        query: LOAD_PROJECTS,
        variables: { published: true, favorite: false },
      });
      if (pubProjectsData && pubProjectsData.projects) {
        const { projects } = pubProjectsData;
        let newItems: ProjectProps[] = [...projects.items];
        let total = projects.total ? projects.total : 0;

        for (const ind in newItems) {
          if (newItems[ind].id === props.id) {
            newItems[ind] = { ...props, isFavorite: !props.isFavorite };
            break;
          }
        }
        cache.writeQuery({
          query: LOAD_PROJECTS,
          variables: { published: true, favorite: false },
          data: {
            projects: {
              items: newItems,
              total,
            },
          },
        });
      }

      const projectsData = cache.readQuery<{ projects: ProjectPayload }>({
        query: LOAD_PROJECTS,
        variables: { published: false, favorite: false },
      });
      if (projectsData && projectsData.projects) {
        const { projects } = projectsData;
        let newItems: ProjectProps[] = [...projects.items];
        let total = projects.total ? projects.total : 0;

        for (const ind in newItems) {
          if (newItems[ind].id === props.id) {
            newItems[ind] = { ...props, isFavorite: !props.isFavorite };
            break;
          }
        }
        cache.writeQuery({
          query: LOAD_PROJECTS,
          variables: { published: false, favorite: false },
          data: {
            projects: {
              items: newItems,
              total,
            },
          },
        });
      }
    },
  });

  const toggleFavorite = () => {
    setFav({ variables: { id: props.id, value: !props.isFavorite } });
  };

  const [setPublished, setPubInfo] = useMutation(SET_PUBLISH, {
    onError: (err: ApolloError) => {
      console.log(err);
      toast({
        title: `Ошибка: ${err.message}`,
        status: "error",
      });
    },
    onCompleted: () => {},
    update: (cache) => {
      const favProjectsData = cache.readQuery<{ projects: ProjectPayload }>({
        query: LOAD_PROJECTS,
        variables: { published: false, favorite: true },
      });
      if (favProjectsData && favProjectsData.projects) {
        const { projects } = favProjectsData;
        let newItems: ProjectProps[] = [...projects.items];
        let total = projects.total ? projects.total : 0;

        for (const ind in newItems) {
          if (newItems[ind].id === props.id) {
            newItems[ind] = { ...props, published: !props.published };
            break;
          }
        }
        cache.writeQuery({
          query: LOAD_PROJECTS,
          variables: { published: false, favorite: true },
          data: {
            projects: {
              items: newItems,
              total,
            },
          },
        });
      }

      const pubProjectsData = cache.readQuery<{ projects: ProjectPayload }>({
        query: LOAD_PROJECTS,
        variables: { published: true, favorite: false },
      });
      if (pubProjectsData && pubProjectsData.projects) {
        const { projects } = pubProjectsData;
        let newItems: ProjectProps[] = [];
        let total = projects.total ? projects.total : 0;
        if (props.published) {
          // delete from favorites
          newItems = projects.items.filter(
            (project: ProjectProps) => project.id !== props.id
          );
          total -= 1;
        } else {
          newItems = [
            { ...props, published: !props.published },
            ...projects.items,
          ];
          total += 1;
        }
        cache.writeQuery({
          query: LOAD_PROJECTS,
          variables: { published: true, favorite: false },
          data: {
            projects: {
              items: newItems,
              total,
            },
          },
        });
      }

      const projectsData = cache.readQuery<{ projects: ProjectPayload }>({
        query: LOAD_PROJECTS,
        variables: { published: false, favorite: false },
      });
      if (projectsData && projectsData.projects) {
        const { projects } = projectsData;
        let newItems: ProjectProps[] = [...projects.items];
        let total = projects.total ? projects.total : 0;

        for (const ind in newItems) {
          if (newItems[ind].id === props.id) {
            newItems[ind] = { ...props, published: !props.published };
            break;
          }
        }
        cache.writeQuery({
          query: LOAD_PROJECTS,
          variables: { published: false, favorite: false },
          data: {
            projects: {
              items: newItems,
              total,
            },
          },
        });
      }
      toast({
        title: props.published
          ? "Проект снят с публикации!"
          : "Проект опубликован!",
        status: "success",
      });
    },
  });

  const togglePublicate = () => {
    setPublished({ variables: { id: props.id, value: !props.published } });
  };

  const loadProject = () => {
    dispatch(setProjectId(props.id));
    dispatch(setModalOpen({ key: ModalsName.PROJECTS, value: false }));
  };

  return (
    <Box pos="relative" w={200} h={200}>
      <VStack
        align="flex-end"
        justify="flex-start"
        position="absolute"
        right=".5rem"
        top=".5rem"
      >
        {
          user &&
            user.id === props.user.id &&
            (setPubInfo.loading ? (
              <Flex w="2rem" h="2rem" justify="center" align="center">
                <Spinner />
              </Flex>
            ) : (
              <Image
                w="2rem"
                h="2rem"
                onClick={() => togglePublicate()}
                src={props.published ? PublishedImage : PublicateImage}
                title={props.published ? "Снять с публикации" : "Опубликовать"}
                cursor="pointer"
              />
            ))
          // <IconButton
          //   isLoading={setPubInfo.loading}
          //   variant="controls"
          //   color={props.published ? "yellow" : "black"}
          //   icon={<ViewIcon boxSize="2rem" />}
          //   aria-label={props.published ? "Снять с публикации" : "Опубликовать"}
          //   onClick={() => togglePublicate()}
          // />
        }
        {user && user.id === props.user.id ? (
          setFavInfo.loading ? (
            <Flex w="2rem" h="2rem" justify="center" align="center">
              <Spinner />
            </Flex>
          ) : (
            <Image
              w="2rem"
              h="2rem"
              onClick={() => toggleFavorite()}
              src={props.isFavorite ? FavoriteImage : AddToFavImage}
              title={
                props.isFavorite
                  ? "Удалить из избранного"
                  : "Добавить в избранное"
              }
              alt={
                props.isFavorite
                  ? "Удалить из избранного"
                  : "Добавить в избранное"
              }
              cursor="pointer"
            />
          )
        ) : (
          props.isFavorite && <Image w="2rem" h="2rem" src={FavoriteImage} />
        )}
        {user &&
          user.id === props.user.id &&
          (deleteInfo.loading ? (
            <Flex w="2rem" h="2rem" justify="center" align="center">
              <Spinner />
            </Flex>
          ) : (
            <Image
              w="2rem"
              h="2rem"
              onClick={() => deleteProject(props.id)}
              src={DeleteImage}
              title="Удалить"
              alt="Удалить"
              cursor="pointer"
            />
          ))}
      </VStack>
      <Image
        src={props.preview}
        alt={props.name}
        maxW={200}
        maxH={150}
        fallback={<Skeleton w={200} h={150} onClick={loadProject} />}
        onClick={loadProject}
      />
    </Box>
  );
};

export default Project;
